
import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import apiClient from '../../services/apiClient';
import { API_ENDPOINTS } from '../../constants/apiEndpoints';

export const getOrganizations = createAsyncThunk(
    'organizations/getOrganizations',
    async (params = {}, { rejectWithValue }) => {
        try {
            const token = localStorage.getItem('access_token');
            if (!token) {
                throw new Error('No token available');
            }

            console.log('Fetching organizations with params:', params);

            const response = await apiClient.get(API_ENDPOINTS.ORGANIZATIONS, { params });
            console.log('Organizations response:', response.data);

            const { status, message, data } = response.data;

            if (status === 200) {
                return {
                    success: true,
                    message: message || 'Organizations retrieved successfully',
                    data: data || {}
                };
            } else {
                return rejectWithValue({
                    message: message || 'Failed to fetch organizations',
                    status: status,
                    apiResponse: response.data
                });
            }
        } catch (error) {
            console.error('Get organizations error:', error);

            let errorMessage = 'Failed to fetch organizations';
            let statusCode = 500;

            if (error.response) {
                statusCode = error.response.status;
                const serverData = error.response.data;
                errorMessage = serverData.message || serverData.error || 'Failed to fetch organizations';
            } else if (error.request) {
                errorMessage = 'Network error. Please check your connection.';
            } else {
                errorMessage = error.message || 'An unexpected error occurred';
            }

            return rejectWithValue({
                message: errorMessage,
                status: statusCode,
                apiResponse: error.response?.data
            });
        }
    }
);

export const getOrganization = createAsyncThunk(
    'organizations/getOrganization',
    async (id, { rejectWithValue }) => {
        try {
            const token = localStorage.getItem('access_token');
            if (!token) {
                throw new Error('No token available');
            }

            console.log('Fetching organization:', id);

            const response = await apiClient.get(API_ENDPOINTS.ORGANIZATION(id));
            console.log('Organization response:', response.data);

            const { status, message, data } = response.data;

            if (status === 200) {
                return {
                    success: true,
                    message: message || 'Organization retrieved successfully',
                    organization: data?.organization || null,
                    stats: data?.stats || null,
                    settings: data?.settings || null,
                    metadata: data?.metadata || null
                };
            } else {
                return rejectWithValue({
                    message: message || 'Failed to fetch organization',
                    status: status,
                    apiResponse: response.data
                });
            }
        } catch (error) {
            console.error('Get organization error:', error);

            let errorMessage = 'Failed to fetch organization';
            let statusCode = 500;

            if (error.response) {
                statusCode = error.response.status;
                const serverData = error.response.data;
                errorMessage = serverData.message || serverData.error || 'Failed to fetch organization';
            } else if (error.request) {
                errorMessage = 'Network error. Please check your connection.';
            } else {
                errorMessage = error.message || 'An unexpected error occurred';
            }

            return rejectWithValue({
                message: errorMessage,
                status: statusCode,
                apiResponse: error.response?.data
            });
        }
    }
);

export const createOrganization = createAsyncThunk(
    'organizations/createOrganization',
    async (organizationData, { rejectWithValue }) => {
        try {
            const token = localStorage.getItem('access_token');
            if (!token) {
                throw new Error('No token available');
            }

            console.log('Creating organization...');
            console.log('Organization data:', organizationData);

            const response = await apiClient.post(API_ENDPOINTS.CREATE_ORGANIZATION, organizationData);
            console.log('Create organization response:', response.data);

            const { status, message, data } = response.data;

            if (status === 201 || status === 200) {
                return {
                    success: true,
                    message: message || 'Organization created successfully',
                    organization: data?.organization || data || null
                };
            } else {
                return rejectWithValue({
                    message: message || 'Failed to create organization',
                    status: status,
                    apiResponse: response.data
                });
            }
        } catch (error) {
            console.error('Create organization error:', error);

            let errorMessage = 'Failed to create organization';
            let statusCode = 500;

            if (error.response) {
                statusCode = error.response.status;
                const serverData = error.response.data;
                errorMessage = serverData.message || serverData.error || 'Failed to create organization';
                
                if (serverData.errors) {
                    const errorMessages = Object.values(serverData.errors).flat().join(', ');
                    errorMessage = errorMessages || errorMessage;
                }
            } else if (error.request) {
                errorMessage = 'Network error. Please check your connection.';
            } else {
                errorMessage = error.message || 'An unexpected error occurred';
            }

            return rejectWithValue({
                message: errorMessage,
                status: statusCode,
                apiResponse: error.response?.data
            });
        }
    }
);

export const updateOrganization = createAsyncThunk(
    'organizations/updateOrganization',
    async ({ id, data }, { rejectWithValue }) => {
        try {
            const token = localStorage.getItem('access_token');
            if (!token) {
                throw new Error('No token available');
            }

            console.log('Updating organization:', id);
            console.log('Update data:', data);

            const response = await apiClient.post(API_ENDPOINTS.UPDATE_ORGANIZATION(id), data);
            console.log('Update organization response:', response.data);

            const { status, message, data: responseData } = response.data;

            if (status === 200) {
                return {
                    success: true,
                    message: message || 'Organization updated successfully',
                    organization: responseData?.organization || responseData || null
                };
            } else {
                return rejectWithValue({
                    message: message || 'Failed to update organization',
                    status: status,
                    apiResponse: response.data
                });
            }
        } catch (error) {
            console.error('Update organization error:', error);

            let errorMessage = 'Failed to update organization';
            let statusCode = 500;

            if (error.response) {
                statusCode = error.response.status;
                const serverData = error.response.data;
                errorMessage = serverData.message || serverData.error || 'Failed to update organization';
                
                if (serverData.errors) {
                    const errorMessages = Object.values(serverData.errors).flat().join(', ');
                    errorMessage = errorMessages || errorMessage;
                }
            } else if (error.request) {
                errorMessage = 'Network error. Please check your connection.';
            } else {
                errorMessage = error.message || 'An unexpected error occurred';
            }

            return rejectWithValue({
                message: errorMessage,
                status: statusCode,
                apiResponse: error.response?.data
            });
        }
    }
);

export const deleteOrganization = createAsyncThunk(
    'organizations/deleteOrganization',
    async (id, { rejectWithValue }) => {
        try {
            const token = localStorage.getItem('access_token');
            if (!token) {
                throw new Error('No token available');
            }

            console.log('Deleting organization:', id);

            const response = await apiClient.delete(API_ENDPOINTS.DELETE_ORGANIZATION(id));
            console.log('Delete organization response:', response.data);

            const { status, message } = response.data;

            if (status === 200) {
                return {
                    success: true,
                    message: message || 'Organization deleted successfully',
                    id: id
                };
            } else {
                return rejectWithValue({
                    message: message || 'Failed to delete organization',
                    status: status,
                    apiResponse: response.data
                });
            }
        } catch (error) {
            console.error('Delete organization error:', error);

            let errorMessage = 'Failed to delete organization';
            let statusCode = 500;

            if (error.response) {
                statusCode = error.response.status;
                const serverData = error.response.data;
                errorMessage = serverData.message || serverData.error || 'Failed to delete organization';
            } else if (error.request) {
                errorMessage = 'Network error. Please check your connection.';
            } else {
                errorMessage = error.message || 'An unexpected error occurred';
            }

            return rejectWithValue({
                message: errorMessage,
                status: statusCode,
                apiResponse: error.response?.data
            });
        }
    }
);

export const syncOrganizationSettings = createAsyncThunk(
    'organizations/syncSettings',
    async ({ organizationId, settings }, { rejectWithValue }) => {
        try {
            const token = localStorage.getItem('access_token');
            if (!token) {
                throw new Error('No token available');
            }

            console.log('Syncing settings for organization:', organizationId);

            const response = await apiClient.post(API_ENDPOINTS.SYNC_ORGANIZATION_SETTINGS(organizationId), {
                settings: settings
            });
            console.log('Sync organization settings response:', response.data);

            const { status, message, data } = response.data;

            if (status === 200) {
                return {
                    success: true,
                    message: message || 'Settings synced successfully',
                    organization: data?.organization || null,
                    settings: data?.settings || []
                };
            } else {
                return rejectWithValue({
                    message: message || 'Failed to sync settings',
                    status: status,
                    apiResponse: response.data
                });
            }
        } catch (error) {
            console.error('Sync organization settings error:', error);

            let errorMessage = 'Failed to sync settings';
            let statusCode = 500;

            if (error.response) {
                statusCode = error.response.status;
                const serverData = error.response.data;
                errorMessage = serverData.message || serverData.error || 'Failed to sync settings';
            } else if (error.request) {
                errorMessage = 'Network error. Please check your connection.';
            } else {
                errorMessage = error.message || 'An unexpected error occurred';
            }

            return rejectWithValue({
                message: errorMessage,
                status: statusCode,
                apiResponse: error.response?.data
            });
        }
    }
);

export const getOrganizationUsers = createAsyncThunk(
    'organizations/getOrganizationUsers',
    async ({ id, params = {} }, { rejectWithValue }) => {
        try {
            const token = localStorage.getItem('access_token');
            if (!token) {
                throw new Error('No token available');
            }

            console.log('Fetching users for organization:', id);

            const response = await apiClient.get(API_ENDPOINTS.ORGANIZATION_USERS(id), { params });
            console.log('Organization users response:', response.data);

            const { status, message, data } = response.data;

            if (status === 200) {
                return {
                    success: true,
                    message: message || 'Organization users retrieved successfully',
                    data: data || {}
                };
            } else {
                return rejectWithValue({
                    message: message || 'Failed to fetch organization users',
                    status: status,
                    apiResponse: response.data
                });
            }
        } catch (error) {
            console.error('Get organization users error:', error);

            let errorMessage = 'Failed to fetch organization users';
            let statusCode = 500;

            if (error.response) {
                statusCode = error.response.status;
                const serverData = error.response.data;
                errorMessage = serverData.message || serverData.error || 'Failed to fetch organization users';
            } else if (error.request) {
                errorMessage = 'Network error. Please check your connection.';
            } else {
                errorMessage = error.message || 'An unexpected error occurred';
            }

            return rejectWithValue({
                message: errorMessage,
                status: statusCode,
                apiResponse: error.response?.data
            });
        }
    }
);

export const getOrganizationStats = createAsyncThunk(
    'organizations/getOrganizationStats',
    async (id, { rejectWithValue }) => {
        try {
            const token = localStorage.getItem('access_token');
            if (!token) {
                throw new Error('No token available');
            }

            console.log('Fetching stats for organization:', id);

            const response = await apiClient.get(API_ENDPOINTS.ORGANIZATION_STATS(id));
            console.log('Organization stats response:', response.data);

            const { status, message, data } = response.data;

            if (status === 200) {
                return {
                    success: true,
                    message: message || 'Organization stats retrieved successfully',
                    stats: data || {}
                };
            } else {
                return rejectWithValue({
                    message: message || 'Failed to fetch organization stats',
                    status: status,
                    apiResponse: response.data
                });
            }
        } catch (error) {
            console.error('Get organization stats error:', error);

            let errorMessage = 'Failed to fetch organization stats';
            let statusCode = 500;

            if (error.response) {
                statusCode = error.response.status;
                const serverData = error.response.data;
                errorMessage = serverData.message || serverData.error || 'Failed to fetch organization stats';
            } else if (error.request) {
                errorMessage = 'Network error. Please check your connection.';
            } else {
                errorMessage = error.message || 'An unexpected error occurred';
            }

            return rejectWithValue({
                message: errorMessage,
                status: statusCode,
                apiResponse: error.response?.data
            });
        }
    }
);

export const getAvailableOrganizations = createAsyncThunk(
    'organizations/getAvailableOrganizations',
    async (_, { rejectWithValue }) => {
        try {
            const token = localStorage.getItem('access_token');
            if (!token) {
                throw new Error('No token available');
            }

            console.log('Fetching available organizations...');

            const response = await apiClient.get(API_ENDPOINTS.AVAILABLE_ORGANIZATIONS);
            console.log('Available organizations response:', response.data);

            const { status, message, data } = response.data;

            if (status === 200) {
                return {
                    success: true,
                    message: message || 'Available organizations retrieved successfully',
                    organizations: data || []
                };
            } else {
                return rejectWithValue({
                    message: message || 'Failed to fetch available organizations',
                    status: status,
                    apiResponse: response.data
                });
            }
        } catch (error) {
            console.error('Get available organizations error:', error);

            let errorMessage = 'Failed to fetch available organizations';
            let statusCode = 500;

            if (error.response) {
                statusCode = error.response.status;
                const serverData = error.response.data;
                errorMessage = serverData.message || serverData.error || 'Failed to fetch available organizations';
            } else if (error.request) {
                errorMessage = 'Network error. Please check your connection.';
            } else {
                errorMessage = error.message || 'An unexpected error occurred';
            }

            return rejectWithValue({
                message: errorMessage,
                status: statusCode,
                apiResponse: error.response?.data
            });
        }
    }
);

export const getOrganizationTypes = createAsyncThunk(
    'organizations/getOrganizationTypes',
    async (_, { rejectWithValue }) => {
        try {
            const token = localStorage.getItem('access_token');
            if (!token) {
                throw new Error('No token available');
            }

            console.log('Fetching organization types...');

            const response = await apiClient.get(API_ENDPOINTS.ORGANIZATION_TYPES);
            console.log('Organization types response:', response.data);

            const { status, message, data } = response.data;

            if (status === 200) {
                return {
                    success: true,
                    message: message || 'Organization types retrieved successfully',
                    types: data?.data || []
                };
            } else {
                return rejectWithValue({
                    message: message || 'Failed to fetch organization types',
                    status: status,
                    apiResponse: response.data
                });
            }
        } catch (error) {
            console.error('Get organization types error:', error);

            let errorMessage = 'Failed to fetch organization types';
            let statusCode = 500;

            if (error.response) {
                statusCode = error.response.status;
                const serverData = error.response.data;
                errorMessage = serverData.message || serverData.error || 'Failed to fetch organization types';
            } else if (error.request) {
                errorMessage = 'Network error. Please check your connection.';
            } else {
                errorMessage = error.message || 'An unexpected error occurred';
            }

            return rejectWithValue({
                message: errorMessage,
                status: statusCode,
                apiResponse: error.response?.data
            });
        }
    }
);

const initialState = {
    organizations: [],
    pagination: {
        current_page: 1,
        last_page: 1,
        per_page: 15,
        total: 0
    },
    currentOrganization: null,
    currentOrganizationStats: null,
    currentOrganizationSettings: null,
    currentOrganizationMetadata: null,
    organizationUsers: [],
    organizationUsersPagination: {
        current_page: 1,
        last_page: 1,
        per_page: 15,
        total: 0
    },
    availableOrganizations: [],
    organizationTypes: [],
    isLoading: false,
    error: null,
    successMessage: null,
    apiMessage: null,
};

const organizationSlice = createSlice({
    name: 'organizations',
    initialState,
    reducers: {
        clearError: (state) => {
            state.error = null;
        },
        clearSuccessMessage: (state) => {
            state.successMessage = null;
        },
        clearApiMessage: (state) => {
            state.apiMessage = null;
        },
        clearOrganizationState: (state) => {
            state.organizations = [];
            state.pagination = {
                current_page: 1,
                last_page: 1,
                per_page: 15,
                total: 0
            };
            state.currentOrganization = null;
            state.currentOrganizationStats = null;
            state.currentOrganizationSettings = null;
            state.currentOrganizationMetadata = null;
            state.organizationUsers = [];
            state.organizationUsersPagination = {
                current_page: 1,
                last_page: 1,
                per_page: 15,
                total: 0
            };
            state.availableOrganizations = [];
            state.organizationTypes = [];
            state.error = null;
            state.successMessage = null;
            state.apiMessage = null;
            state.isLoading = false;
        },
    },
    extraReducers: (builder) => {
        builder
            // ===== GET ORGANIZATIONS =====
            .addCase(getOrganizations.pending, (state) => {
                state.isLoading = true;
                state.error = null;
                state.successMessage = null;
                state.apiMessage = null;
            })
            .addCase(getOrganizations.fulfilled, (state, action) => {
                state.isLoading = false;
                const payload = action.payload || {};
                const responseData = payload.data || {};
                state.organizations = responseData.data || [];
                state.pagination = responseData.meta || {
                    current_page: 1,
                    last_page: 1,
                    per_page: 15,
                    total: 0
                };
                state.successMessage = payload.message || 'Organizations retrieved successfully';
                state.apiMessage = payload.message || 'Organizations retrieved successfully';
                state.error = null;
            })
            .addCase(getOrganizations.rejected, (state, action) => {
                state.isLoading = false;
                state.error = action.payload?.message || 'Failed to fetch organizations';
                state.apiMessage = action.payload?.message || 'Failed to fetch organizations';
                state.successMessage = null;
                state.organizations = [];
                state.pagination = {
                    current_page: 1,
                    last_page: 1,
                    per_page: 15,
                    total: 0
                };
            })

            // ===== GET ORGANIZATION =====
            .addCase(getOrganization.pending, (state) => {
                state.isLoading = true;
                state.error = null;
                state.successMessage = null;
                state.apiMessage = null;
            })
            .addCase(getOrganization.fulfilled, (state, action) => {
                state.isLoading = false;
                const payload = action.payload || {};
                state.currentOrganization = payload.organization || null;
                state.currentOrganizationStats = payload.stats || null;
                state.currentOrganizationSettings = payload.settings || null;
                state.currentOrganizationMetadata = payload.metadata || null;
                state.successMessage = payload.message || 'Organization retrieved successfully';
                state.apiMessage = payload.message || 'Organization retrieved successfully';
                state.error = null;
            })
            .addCase(getOrganization.rejected, (state, action) => {
                state.isLoading = false;
                state.error = action.payload?.message || 'Failed to fetch organization';
                state.apiMessage = action.payload?.message || 'Failed to fetch organization';
                state.successMessage = null;
                state.currentOrganization = null;
                state.currentOrganizationStats = null;
                state.currentOrganizationSettings = null;
                state.currentOrganizationMetadata = null;
            })

            // ===== CREATE ORGANIZATION =====
            .addCase(createOrganization.pending, (state) => {
                state.isLoading = true;
                state.error = null;
                state.successMessage = null;
                state.apiMessage = null;
            })
            .addCase(createOrganization.fulfilled, (state, action) => {
                state.isLoading = false;
                const payload = action.payload || {};
                state.successMessage = payload.message || 'Organization created successfully';
                state.apiMessage = payload.message || 'Organization created successfully';
                state.error = null;
                if (payload.organization) {
                    state.organizations = [payload.organization, ...state.organizations];
                }
            })
            .addCase(createOrganization.rejected, (state, action) => {
                state.isLoading = false;
                state.error = action.payload?.message || 'Failed to create organization';
                state.apiMessage = action.payload?.message || 'Failed to create organization';
                state.successMessage = null;
            })

            // ===== UPDATE ORGANIZATION =====
            .addCase(updateOrganization.pending, (state) => {
                state.isLoading = true;
                state.error = null;
                state.successMessage = null;
                state.apiMessage = null;
            })
            .addCase(updateOrganization.fulfilled, (state, action) => {
                state.isLoading = false;
                const payload = action.payload || {};
                state.successMessage = payload.message || 'Organization updated successfully';
                state.apiMessage = payload.message || 'Organization updated successfully';
                state.error = null;
                if (payload.organization) {
                    state.organizations = state.organizations.map(org => 
                        org.id === payload.organization.id ? payload.organization : org
                    );
                    if (state.currentOrganization?.id === payload.organization.id) {
                        state.currentOrganization = payload.organization;
                    }
                }
            })
            .addCase(updateOrganization.rejected, (state, action) => {
                state.isLoading = false;
                state.error = action.payload?.message || 'Failed to update organization';
                state.apiMessage = action.payload?.message || 'Failed to update organization';
                state.successMessage = null;
            })

            // ===== DELETE ORGANIZATION =====
            .addCase(deleteOrganization.pending, (state) => {
                state.isLoading = true;
                state.error = null;
                state.successMessage = null;
                state.apiMessage = null;
            })
            .addCase(deleteOrganization.fulfilled, (state, action) => {
                state.isLoading = false;
                const payload = action.payload || {};
                state.successMessage = payload.message || 'Organization deleted successfully';
                state.apiMessage = payload.message || 'Organization deleted successfully';
                state.error = null;
                if (payload.id) {
                    state.organizations = state.organizations.filter(org => org.id !== payload.id);
                    if (state.currentOrganization?.id === payload.id) {
                        state.currentOrganization = null;
                        state.currentOrganizationStats = null;
                        state.currentOrganizationSettings = null;
                        state.currentOrganizationMetadata = null;
                    }
                }
            })
            .addCase(deleteOrganization.rejected, (state, action) => {
                state.isLoading = false;
                state.error = action.payload?.message || 'Failed to delete organization';
                state.apiMessage = action.payload?.message || 'Failed to delete organization';
                state.successMessage = null;
            })

            // ===== SYNC ORGANIZATION SETTINGS =====
            .addCase(syncOrganizationSettings.pending, (state) => {
                state.isLoading = true;
                state.error = null;
                state.successMessage = null;
                state.apiMessage = null;
            })
            .addCase(syncOrganizationSettings.fulfilled, (state, action) => {
                state.isLoading = false;
                const payload = action.payload || {};
                state.currentOrganization = payload.organization || null;
                state.currentOrganizationSettings = payload.settings || null;
                state.successMessage = payload.message || 'Settings synced successfully';
                state.apiMessage = payload.message || 'Settings synced successfully';
                state.error = null;
            })
            .addCase(syncOrganizationSettings.rejected, (state, action) => {
                state.isLoading = false;
                state.error = action.payload?.message || 'Failed to sync settings';
                state.apiMessage = action.payload?.message || 'Failed to sync settings';
                state.successMessage = null;
            })

            // ===== GET ORGANIZATION USERS =====
            .addCase(getOrganizationUsers.pending, (state) => {
                state.isLoading = true;
                state.error = null;
                state.successMessage = null;
                state.apiMessage = null;
            })
            .addCase(getOrganizationUsers.fulfilled, (state, action) => {
                state.isLoading = false;
                const payload = action.payload || {};
                const responseData = payload.data || {};
                state.organizationUsers = responseData.data || [];
                state.organizationUsersPagination = responseData.meta || {
                    current_page: 1,
                    last_page: 1,
                    per_page: 15,
                    total: 0
                };
                state.successMessage = payload.message || 'Organization users retrieved successfully';
                state.apiMessage = payload.message || 'Organization users retrieved successfully';
                state.error = null;
            })
            .addCase(getOrganizationUsers.rejected, (state, action) => {
                state.isLoading = false;
                state.error = action.payload?.message || 'Failed to fetch organization users';
                state.apiMessage = action.payload?.message || 'Failed to fetch organization users';
                state.successMessage = null;
                state.organizationUsers = [];
                state.organizationUsersPagination = {
                    current_page: 1,
                    last_page: 1,
                    per_page: 15,
                    total: 0
                };
            })

            // ===== GET ORGANIZATION STATS =====
            .addCase(getOrganizationStats.pending, (state) => {
                state.isLoading = true;
                state.error = null;
                state.successMessage = null;
                state.apiMessage = null;
            })
            .addCase(getOrganizationStats.fulfilled, (state, action) => {
                state.isLoading = false;
                const payload = action.payload || {};
                state.currentOrganizationStats = payload.stats || null;
                state.successMessage = payload.message || 'Organization stats retrieved successfully';
                state.apiMessage = payload.message || 'Organization stats retrieved successfully';
                state.error = null;
            })
            .addCase(getOrganizationStats.rejected, (state, action) => {
                state.isLoading = false;
                state.error = action.payload?.message || 'Failed to fetch organization stats';
                state.apiMessage = action.payload?.message || 'Failed to fetch organization stats';
                state.successMessage = null;
                state.currentOrganizationStats = null;
            })

            // ===== GET AVAILABLE ORGANIZATIONS =====
            .addCase(getAvailableOrganizations.pending, (state) => {
                state.isLoading = true;
                state.error = null;
                state.successMessage = null;
                state.apiMessage = null;
            })
            .addCase(getAvailableOrganizations.fulfilled, (state, action) => {
                state.isLoading = false;
                const payload = action.payload || {};
                state.availableOrganizations = payload.organizations || [];
                state.successMessage = payload.message || 'Available organizations retrieved successfully';
                state.apiMessage = payload.message || 'Available organizations retrieved successfully';
                state.error = null;
            })
            .addCase(getAvailableOrganizations.rejected, (state, action) => {
                state.isLoading = false;
                state.error = action.payload?.message || 'Failed to fetch available organizations';
                state.apiMessage = action.payload?.message || 'Failed to fetch available organizations';
                state.successMessage = null;
                state.availableOrganizations = [];
            })

            // ===== GET ORGANIZATION TYPES =====
            .addCase(getOrganizationTypes.pending, (state) => {
                state.isLoading = true;
                state.error = null;
                state.successMessage = null;
                state.apiMessage = null;
            })
            .addCase(getOrganizationTypes.fulfilled, (state, action) => {
                state.isLoading = false;
                const payload = action.payload || {};
                state.organizationTypes = payload.types || [];
                state.successMessage = payload.message || 'Organization types retrieved successfully';
                state.apiMessage = payload.message || 'Organization types retrieved successfully';
                state.error = null;
            })
            .addCase(getOrganizationTypes.rejected, (state, action) => {
                state.isLoading = false;
                state.error = action.payload?.message || 'Failed to fetch organization types';
                state.apiMessage = action.payload?.message || 'Failed to fetch organization types';
                state.successMessage = null;
                state.organizationTypes = [];
            });
    },
});

export const {
    clearError,
    clearSuccessMessage,
    clearApiMessage,
    clearOrganizationState,
} = organizationSlice.actions;

export const selectOrganizations = (state) => state.organizations?.organizations || [];
export const selectOrganizationPagination = (state) => state.organizations?.pagination || {
    current_page: 1,
    last_page: 1,
    per_page: 15,
    total: 0
};
export const selectCurrentOrganization = (state) => state.organizations?.currentOrganization || null;
export const selectCurrentOrganizationStats = (state) => state.organizations?.currentOrganizationStats || null;
export const selectCurrentOrganizationSettings = (state) => state.organizations?.currentOrganizationSettings || null;
export const selectCurrentOrganizationMetadata = (state) => state.organizations?.currentOrganizationMetadata || null;
export const selectOrganizationUsers = (state) => state.organizations?.organizationUsers || [];
export const selectOrganizationUsersPagination = (state) => state.organizations?.organizationUsersPagination || {
    current_page: 1,
    last_page: 1,
    per_page: 15,
    total: 0
};
export const selectAvailableOrganizations = (state) => state.organizations?.availableOrganizations || [];
export const selectOrganizationTypes = (state) => state.organizations?.organizationTypes || [];
export const selectOrganizationsLoading = (state) => state.organizations?.isLoading || false;
export const selectOrganizationsError = (state) => state.organizations?.error || null;
export const selectOrganizationsSuccess = (state) => state.organizations?.successMessage || null;
export const selectOrganizationsApiMessage = (state) => state.organizations?.apiMessage || null;

export default organizationSlice.reducer;